import * as THREE from 'three';

export type Bearing = 'ahead' | 'left' | 'right' | 'behind';

export type WaypointSnap = {
  label: string;
  x: number;
  z: number;
  dist: number;
  bearing: Bearing;
  targetYaw: number;
  signedDeg: number;
};

/** Signed turn in (−180, 180]. Negative = left of facing. */
export function signedBearingDeg(yaw: number, targetYaw: number): number {
  let d = ((targetYaw - yaw) * 180) / Math.PI;
  while (d > 180) {
    d -= 360;
  }
  while (d <= -180) {
    d += 360;
  }
  return d;
}

export function bearingOf(signedDeg: number): Bearing {
  const a = Math.abs(signedDeg);
  if (a <= 35) {
    return 'ahead';
  }
  if (a >= 145) {
    return 'behind';
  }
  return signedDeg < 0 ? 'left' : 'right';
}

export function waypointSnap(
  player: { x: number; z: number },
  yaw: number,
  target: { x: number; z: number; label: string },
): WaypointSnap {
  const dx = target.x - player.x;
  const dz = target.z - player.z;
  const dist = Math.hypot(dx, dz);
  const targetYaw = Math.atan2(-dx, -dz);
  const signedDeg = signedBearingDeg(yaw, targetYaw);
  return {
    label: target.label,
    x: target.x,
    z: target.z,
    dist,
    bearing: bearingOf(signedDeg),
    targetYaw,
    signedDeg,
  };
}

export function bearingCopy(snap: WaypointSnap): string {
  const meters = String(Math.round(snap.dist));
  const dir =
    snap.bearing === 'ahead'
      ? 'ahead'
      : snap.bearing === 'behind'
        ? 'behind you'
        : snap.bearing === 'left'
          ? 'to your left'
          : 'to your right';
  return `${snap.label} · ${meters} m · ${dir}`;
}

/** Hide the world pin once Rowan is standing on the objective so it cannot clip him. */
export const WAYPOINT_HIDE_M = 3.6;
const MARKER_Y = 3.2;

function markMat(color: number): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color,
    depthTest: true,
    depthWrite: false,
    transparent: true,
    opacity: 0.92,
  });
}

/** Floating ring above the current objective. No ground stem — a stem clips through Rowan. */
export class WaypointMarker {
  readonly root: THREE.Group;
  private readonly head: THREE.Mesh;

  constructor(scene: THREE.Scene) {
    this.root = new THREE.Group();
    this.root.name = 'WAYPOINT';
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.32, 0.04, 8, 18), markMat(0xffe08a));
    ring.position.y = MARKER_Y;
    ring.rotation.x = Math.PI / 2;
    const head = new THREE.Mesh(new THREE.OctahedronGeometry(0.14, 0), markMat(0xfff3c0));
    head.position.y = MARKER_Y;
    this.head = head;
    this.root.add(ring, head);
    this.root.renderOrder = 4;
    this.root.visible = false;
    scene.add(this.root);
  }

  set(target: { x: number; z: number; dist?: number } | null): void {
    if (!target || (typeof target.dist === 'number' && target.dist < WAYPOINT_HIDE_M)) {
      this.root.visible = false;
      return;
    }
    this.root.visible = true;
    this.root.position.set(target.x, 0.05, target.z);
    this.head.rotation.y += 0.03;
    this.head.position.y = MARKER_Y + Math.sin(this.head.rotation.y * 2) * 0.08;
  }
}
